import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Switch, TextField, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { EWebhookFormat, WebhookService, WebhookSubscription } from '../services/webhook.service';
import { WebhookEventPicker } from './webhook-event-picker';
import { integrationFieldSx, switchRowSx } from './integration-list.styles';

interface Props {
    edit: WebhookSubscription | null;
    onClose: () => void;
    onSaved: () => void;
}

export const WebhookDialog = ({ edit, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [events, setEvents] = useState<number[]>([]);
    const [active, setActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState(false);

    /// O segredo aparece só na criação; depois o servidor devolve os 4 últimos caracteres.
    const [secret, setSecret] = useState<string | null>(null);

    useEffect(() => {
        setName(edit?.name ?? '');
        setUrl(edit?.url ?? '');
        setEvents(edit?.events ?? []);
        setActive(edit?.active ?? true);
        setSecret(null);
        setTouched(false);
    }, [edit]);

    const field = integrationFieldSx(cc);

    const save = async () => {
        setTouched(true);

        if (!name.trim() || !url.trim() || events.length === 0) return;

        setSaving(true);

        try {
            const service = new WebhookService();
            const body = {
                name: name.trim(), url: url.trim(), events, active, format: EWebhookFormat.Json,
            };

            if (edit) {
                await service.update(edit.id, body);
                showAlert(t('general.atualizacao'), 'success', 2500);
                onSaved();
                return;
            }

            const created = await service.create(body);

            showAlert(t('general.cadastro'), 'success', 2500);

            // Fica na tela mostrando o segredo: fechar e avisar depois deixaria sem a única cópia.
            setSecret(created.data.data?.secretHint ?? null);
            onSaved();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t(edit ? 'webhooks.editar' : 'webhooks.novo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {t('webhooks.ajuda')}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ display: 'grid', gap: 1, pt: '16px !important' }}>
                <TextField
                    required
                    autoFocus
                    size="small"
                    label={t('webhooks.nome')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={Boolean(touched && !name.trim())}
                    helperText={touched && !name.trim() ? t('webhooks.informeONome') : ' '}
                    sx={field}
                />

                <TextField
                    required
                    size="small"
                    label={t('webhooks.url')}
                    placeholder="https://"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    error={Boolean(touched && !url.trim())}
                    helperText={touched && !url.trim() ? t('webhooks.informeAUrl') : ' '}
                    sx={field}
                />

                <WebhookEventPicker
                    value={events}
                    error={touched && events.length === 0}
                    onChange={setEvents}
                />

                <Box sx={{ ...switchRowSx(cc), mt: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary }}>
                        {t('webhooks.ativa')}
                    </Typography>
                    <Switch checked={active} onChange={() => setActive((v) => !v)} />
                </Box>

                {edit && (
                    <Typography sx={{ fontSize: 12, color: cc.textMuted }}>
                        {t('webhooks.segredoTerminaEm', { fim: edit.secretHint })}
                    </Typography>
                )}

                {secret && (
                    <Box
                        sx={{
                            border: `1px solid ${cc.accent}`,
                            borderRadius: '8px',
                            p: 1.5,
                            bgcolor: cc.accentSoftBg,
                        }}
                    >
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: cc.accent }}>
                            {t('webhooks.segredoCriado')}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: cc.textSecondary, mt: 0.5 }}>
                            {t('webhooks.segredoAjuda')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="success"
                    onClick={save}
                    disabled={saving}
                >
                    {t('general.salvar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
